import { fmtLocalDate } from '../../../utils/datetime';
import { valueLabel, type Field, type QcRow, type Stat, type TestDef } from './controlChartData';

// ─────────────────────────────────────────────────────────────────────────────
// Chart / data set export, ported from the prototype's export chain.
//
// These produce REAL files. An export button that reports "Exported
// successfully" without writing anything is worse than no button: the user
// believes they have the data and finds out later that they do not.
//
// Formats:
//   · Image — the live SVG rasterised to PNG at 2x, in the chart's current theme.
//   · Excel — a SpreadsheetML workbook (.xls) with the THREE sheets the
//     prototype produces: QC Results, Statistics, Definition. SpreadsheetML is
//     plain XML that Excel opens natively, so it needs no library.
//   · PDF   — the browser's print dialog on a clean, chrome-free window
//     ("Save as PDF"), which is what the prototype does.
// ─────────────────────────────────────────────────────────────────────────────

const esc = (s: unknown) =>
  String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[c]!));

/** Hands the browser a file. Uses an object URL + a synthetic click, revoked
 *  afterwards so a long session does not leak blobs. */
export function download(name: string, data: string, mime: string) {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export interface Col<T> { h: string; f: (r: T) => string | number | null }

/** RFC-4180: quote a field that contains a comma, quote or newline, and double
 *  any embedded quote. Without this a parameter named "Nitrate, total" silently
 *  splits into two columns. */
function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function csvOf<T>(cols: Col<T>[], rows: T[]): string {
  return [cols.map(c => csvCell(c.h)).join(','),
    ...rows.map(r => cols.map(c => csvCell(c.f(r))).join(','))].join('\r\n');
}

interface Sheet<T> { name: string; cols: Col<T>[]; rows: T[] }

/** SpreadsheetML 2003 — a multi-sheet workbook Excel opens natively, with no
 *  third-party dependency. Numbers are typed so Excel does not left-align them
 *  as text (a control limit that sorts as a string is worse than useless). */
export function xlsOf(sheets: Sheet<never>[]): string {
  const cell = (v: unknown) => {
    const num = typeof v === 'number' && Number.isFinite(v);
    return `<Cell><Data ss:Type="${num ? 'Number' : 'String'}">${esc(v ?? '')}</Data></Cell>`;
  };
  const body = sheets.map(s => `
  <Worksheet ss:Name="${esc(s.name).slice(0, 31)}">
   <Table>
    <Row>${s.cols.map(c => `<Cell><Data ss:Type="String">${esc(c.h)}</Data></Cell>`).join('')}</Row>
    ${s.rows.map(r => `<Row>${s.cols.map(c => cell(c.f(r))).join('')}</Row>`).join('\n    ')}
   </Table>
  </Worksheet>`).join('');
  return `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">${body}
</Workbook>`;
}

// ── column sets (the prototype's) ────────────────────────────────────────────

export const RESULT_COLS: Col<QcRow>[] = [
  { h: 'Analyzed', f: r => fmtLocalDate(r.d) },
  { h: 'Test', f: r => r.test },
  { h: 'Method', f: r => r.method },
  { h: 'Matrix', f: r => r.matrix },
  { h: 'Parameter', f: r => r.p },
  { h: 'QC Type', f: r => r.qt },
  { h: 'QC Role', f: r => r.qr },
  { h: '% Recovery', f: r => r.rec },
  { h: 'RPD', f: r => r.rpd },
  { h: 'Result', f: r => r.res },
  { h: 'Count 1', f: r => r.c1 ?? null },
  { h: 'Count 2', f: r => r.c2 ?? null },
  { h: 'R (log)', f: r => r.rng ?? null },
  { h: 'log10 count', f: r => r.logc ?? null },
  { h: 'Colonies', f: r => r.growth ?? null },
  { h: 'QC Batch', f: r => r.batch },
  { h: 'Sample', f: r => r.sample },
  { h: 'Instrument', f: r => r.inst },
  { h: 'Analyst', f: r => r.anly },
];

export const STAT_COLS: Col<Stat>[] = [
  { h: 'Parameter', f: s => s.parameter },
  { h: 'Chart kind', f: s => s.kind },
  { h: 'Mean', f: s => s.mean },
  { h: 'STD', f: s => s.std },
  { h: 'UCL', f: s => s.ucl },
  { h: 'UWL', f: s => s.uwl },
  { h: 'LWL', f: s => s.lwl },
  { h: 'LCL', f: s => s.lcl },
  { h: 'Count', f: s => s.count },
];

export interface DefRow { k: string; v: string }
export const DEF_COLS: Col<DefRow>[] = [
  { h: 'Setting', f: r => r.k },
  { h: 'Value', f: r => r.v },
];

/** The chart DEFINITION sheet — what was charted, over what window, with which
 *  filters. An exported data set without its definition cannot be reproduced,
 *  which is exactly what an auditor asks for. */
export function defRows(o: {
  ccid: string; test: TestDef | null; field: Field; qcTypes: string[]; params: string[];
  from: string; to: string; inst: string[]; anly: string[]; role: string;
  nRows: number; nParams: number; signedBy?: string; hash?: string;
}): DefRow[] {
  return [
    { k: 'Chart ID', v: o.ccid || '(unsaved)' },
    { k: 'Matrix', v: o.test?.matrix ?? '' },
    { k: 'Test', v: o.test?.test ?? '' },
    { k: 'Method', v: o.test?.method ?? '' },
    { k: 'Statistic charted', v: valueLabel(o.field) },
    { k: 'QC role', v: o.role || 'all roles' },
    { k: 'QC type(s)', v: o.qcTypes.join(', ') || 'all' },
    { k: 'Parameter(s)', v: o.params.join(', ') || 'all' },
    { k: 'Instrument(s)', v: o.inst.join(', ') || 'all' },
    { k: 'Analyst(s)', v: o.anly.join(', ') || 'all' },
    { k: 'Date from', v: fmtLocalDate(o.from) },
    { k: 'Date to', v: fmtLocalDate(o.to) },
    { k: 'QC results', v: String(o.nRows) },
    { k: 'Parameters charted', v: String(o.nParams) },
    { k: 'Signed by', v: o.signedBy || '(unsigned)' },
    { k: 'Signature fingerprint', v: o.hash || '' },
  ];
}

/** A filename stem that identifies the export without a lookup: test, QC types
 *  and the window. */
export function stem(test: TestDef | null, qcTypes: string[], from: string, to: string): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return safe([test?.test ?? 'ControlChart', qcTypes.join('-') || 'allQC',
    from.replace(/-/g, ''), to.replace(/-/g, '')].filter(Boolean).join('_'));
}

// ── image + print ────────────────────────────────────────────────────────────

/** Rasterises the live SVG to PNG at 2x. The SVG is serialised and drawn onto a
 *  canvas rather than screenshotted, so the export is the chart itself at
 *  whatever resolution is asked for, not a blurry copy of the screen. */
export function svgToPng(svg: SVGSVGElement, name: string, scale = 2): Promise<void> {
  return new Promise((resolve, reject) => {
    const box = svg.viewBox.baseVal;
    const w = box && box.width ? box.width : svg.clientWidth || 1000;
    const h = box && box.height ? box.height : svg.clientHeight || 400;

    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', String(w));
    clone.setAttribute('height', String(h));
    const src = new XMLSerializer().serializeToString(clone);
    // Encode as a data URI rather than a blob URL: some browsers taint the
    // canvas for blob-sourced SVG, which then makes toBlob throw on export.
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(src)}`;

    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = Math.round(w * scale);
      c.height = Math.round(h * scale);
      const ctx = c.getContext('2d');
      if (!ctx) return reject(new Error('Canvas is unavailable in this browser.'));
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      c.toBlob(b => {
        if (!b) return reject(new Error('The chart image could not be produced.'));
        const u = URL.createObjectURL(b);
        const a = document.createElement('a');
        a.href = u;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 4000);
        resolve();
      }, 'image/png');
    };
    img.onerror = () => reject(new Error('The chart image could not be rendered.'));
    img.src = url;
  });
}

/** Opens the chart in its own window for preview and print — the same thing the
 *  dashboard cards do. The window is built by cloning the LIVE chart, so the
 *  preview is the chart itself and not a redrawn approximation. Everything
 *  interactive is stripped: it is a document, not a second workbench.
 *
 *  Returns false when the pop-up was blocked, so the caller can say so rather
 *  than leaving the user staring at nothing. */
export function popoutChart(nodes: (Element | null)[], head: string, sub: string, print = false): boolean {
  const w = window.open('', `ccpreview_${Date.now()}`, 'width=1120,height=820,scrollbars=yes,resizable=yes');
  if (!w) return false;

  const parts = nodes.filter(Boolean).map(n => {
    const c = n!.cloneNode(true) as Element;
    c.querySelectorAll('button, input, select').forEach(el => el.remove());
    return c.outerHTML;
  }).join('\n');

  const css = [...document.querySelectorAll('style')].map(s => s.textContent).join('\n');
  w.document.open();
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(head)}</title><style>${css}
     body{margin:0;background:#fff;font-family:Tahoma,Verdana,"Segoe UI",sans-serif;font-size:12px;color:#1f2430}
     .pbar{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:11px 20px;
       border-bottom:1px solid #e3e6ea}
     .pbar h1{font-size:15px;margin:0}.pbar .s{color:#6b7280;font-size:11.5px;margin-top:2px}
     .pbody{padding:16px 20px}
     @media print{.pbar{border-bottom:1px solid #ccc}}
     </style></head><body>
     <div class="pbar"><div><h1>${esc(head)}</h1><div class="s">${esc(sub)}</div></div></div>
     <div class="pbody">${parts}</div></body></html>`);
  w.document.close();
  if (print) w.setTimeout(() => w.print(), 350);
  return true;
}
